import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent,
  Button, IconButton, Typography, Box, Grid, TextField,
  MenuItem, CircularProgress, Chip, InputAdornment, Autocomplete
} from '@mui/material';
import { Close, Delete, ShoppingCart, Search } from '@mui/icons-material';
import { fetchData, postData, updateData } from '../../../apiService';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';

const SaleReturnModal = ({
  open, onClose, companyId, branchId, token, customers = [],
  setLoading, setLoadingMessage, setError, setSuccess,
  fetchSaleReturns, userId, initialBillData = {},
  mode = 'create', editData = null
}) => {
  const { logAudit } = useAuditLog();
  const [formData, setFormData] = useState({
    billNo: '', billPrefix: '', finYear: '',
    billDate: new Date().toISOString().split('T')[0],
    saleId: '', saleNo: '', customerId: '', customerName: '',
    gstNo: '', invType: '', invoiceTypeId: null, gstType: 'Excluding Tax', remarks: ''
  });
  const [sales, setSales] = useState([]);
  const [items, setItems] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [additionalCharges, setAdditionalCharges] = useState({
    freight: 0, packing: 0, tcsAmount: 0, paidAmount: 0
  });
  const [summary, setSummary] = useState({
    subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0,
    freightGST: 0, packingGST: 0, effectiveGSTRate: 18, tcs: 0, roundOff: 0, total: 0
  });

  const isIGSTInvoice = () => {
    const t = (formData.invType || '').toLowerCase();
    if (t.includes('b to b') || t.includes('b to c') || t.includes('b2b') || t.includes('b2c')) return false;
    return t.includes('igst') || t.length > 0;
  };
  const isIncludingTax = () => formData.gstType === 'Including Tax';

  // Fetch items list
  useEffect(() => {
    if (!companyId || !token || !open) return;
    fetchData('ItemApi', `/${companyId}`, token)
      .then(res => setItemsList(Array.isArray(res.response_data || res) ? (res.response_data || res) : []))
      .catch(() => setItemsList([]));
  }, [companyId, token, open]);

  // Set bill data from parent when modal opens
  useEffect(() => {
    if (!open || !initialBillData?.billNumber) return;
    setFormData(prev => ({
      ...prev,
      billNo:     String(initialBillData.billNumber),
      billPrefix: initialBillData.billPrefix || '',
      finYear:    initialBillData.finYear    || '',
    }));
  }, [open, initialBillData?.billNumber, initialBillData?.billPrefix, initialBillData?.finYear]); // eslint-disable-line

  // Populate form in edit mode
  useEffect(() => {
    if (!open || mode !== 'edit' || !editData) return;
    const sr = editData.SaleReturn || editData;
    const details = editData.SaleReturnDetails || [];
    setFormData({
      billNo:        String(sr.BillNumber || ''),
      billPrefix:    sr.BillPrefix || '',
      finYear:       sr.FinYear || '',
      billDate:      sr.BillDate ? sr.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
      saleId:        sr.SaleId || '',
      saleNo:        sr.SaleNo || '',
      customerId:    sr.MasterId || '',
      customerName:  sr.CustomerName || '',
      gstNo:         sr.GSTNumber || '',
      invType:       sr.InvoiceTypeName || '',
      invoiceTypeId: sr.InvoiceTypeId || null,
      gstType:       sr.GstCalculationType === 2 ? 'Including Tax' : 'Excluding Tax',
      remarks:       sr.Remarks || ''
    });
    setAdditionalCharges({
      freight:    sr.FreightAmount    || 0,
      packing:    sr.PackingAmount    || 0,
      tcsAmount:  sr.TCSAmount        || 0,
      paidAmount: sr.PaidAmount       || 0,
    });
    const mapped = details.map(d => ({
      id: Date.now() + Math.random(),
      itemId:       d.ItemId,
      item:         d.ItemName || '',
      hsnCode:      d.HSNCode || '',
      gstPercent:   d.GSTRate || 0,
      purchasedQty: parseFloat(d.SaledQuantity) || 0,
      returnedQty:  parseFloat(d.PrevQuantity) || 0,
      balanceStock: (parseFloat(d.BalanceStock) || 0) + (parseFloat(d.Quantity) || 0),
      rcvdQty:      parseFloat(d.Quantity) || 0,
      rate:         d.Rate || 0,
      discPercent:  d.DiscountPercentage || 0,
      discAmt:      d.DiscountAmount || 0,
      taxable:      d.TaxableAmount || 0,
      cgst:         d.CGSTAmount || 0,
      sgst:         d.SGSTAmount || 0,
      igst:         d.IGSTAmount || 0,
      total:        d.TotalAmount || 0,
      detailId:     d.Id || 0,
    }));
    setItems(mapped);
    calculateSummary(mapped, {
      freight: sr.FreightAmount || 0, packing: sr.PackingAmount || 0,
      tcsAmount: sr.TCSAmount || 0, paidAmount: sr.PaidAmount || 0
    });

    // Fetch sales list for the customer so Against Sale dropdown is populated
    if (sr.MasterId) {
      fetchCustomerSales(sr.MasterId).then(() => {
        // If sales list doesn't include the current sale, add it as fallback
        if (sr.SaleId) {
          setSales(prev => {
            const exists = prev.some(s => (s.Id || s.SaleId) === sr.SaleId);
            if (!exists) {
              return [...prev, {
                Id: sr.SaleId, SaleId: sr.SaleId,
                BillPrefix: '', BillNumber: sr.SaleNo || sr.SaleId,
                BillDate: '', TotalAmount: 0
              }];
            }
            return prev;
          });
        }
      });
    }
  }, [open, mode, editData]); // eslint-disable-line



  // Recalculate when invType or gstType changes
  useEffect(() => {
    if (items.length > 0) recalcItems(items);
  }, [formData.invType, formData.gstType]); // eslint-disable-line react-hooks/exhaustive-deps

  const recalcItems = (list) => {
    const useIGST = isIGSTInvoice();
    const updated = list.map(item => {
      const qty = parseFloat(item.rcvdQty) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discPct = parseFloat(item.discPercent) || 0;
      const gstRate = parseFloat(item.gstPercent) || 0;
      const base = isIncludingTax() ? (qty * rate) / (1 + gstRate / 100) : qty * rate;
      const discAmt = (base * discPct) / 100;
      const taxable = base - discAmt;
      const gstAmt = (taxable * gstRate) / 100;
      return {
        ...item,
        discAmt: Math.round(discAmt * 100) / 100,
        taxable: Math.round(taxable * 100) / 100,
        cgst: useIGST ? 0 : Math.round((gstAmt / 2) * 100) / 100,
        sgst: useIGST ? 0 : Math.round((gstAmt / 2) * 100) / 100,
        igst: useIGST ? Math.round(gstAmt * 100) / 100 : 0,
        total: Math.round((taxable + gstAmt) * 100) / 100
      };
    });
    setItems(updated);
    calculateSummary(updated, additionalCharges);
  };

  const fetchCustomerSales = async (customerId) => {
    if (!customerId) { setSales([]); return; }
    setSalesLoading(true);
    try {
      const res = await fetchData('SaleReturnApi', `/PartyWiseSaleList/${companyId}/${customerId}`, token);
      let data = res.response_data || res || [];
      setSales(data);
    } catch (e) { console.error(e); setSales([]); }
    setSalesLoading(false);
  };

  const handleSaleChange = async (saleId) => {
    if (!saleId) {
      setFormData(prev => ({ ...prev, saleId: '', saleNo: '' }));
      setItems([]);
      return;
    }
    try {
      const res = await fetchData('SaleReturnApi', `/PartySaledetails/${companyId}/${saleId}`, token);
      const data = res.response_data || res;
      // Response: { Sale: {...}, SaleDetails: [...] }
      const sale = data.Sale || data;
      const details = data.SaleDetails || [];
      console.log('SaleDetails first item:', details[0]);

      setFormData(prev => ({
        ...prev,
        saleId,
        saleNo: `${sale.BillPrefix ? sale.BillPrefix + '/' : ''}${sale.BillNumber || ''}${sale.FinYear ? '/' + sale.FinYear : ''}`,
        invType: sale.InvoiceTypeName || prev.invType,
        invoiceTypeId: sale.InvoiceTypeId || sale.invoiceTypeId || prev.invoiceTypeId,
        gstNo: sale.GSTNumber || prev.gstNo,
        gstType: sale.GstCalculationType === 2 ? 'Including Tax' : 'Excluding Tax'
      }));

      const mapped = details.map(d => {
        const itemFromList = itemsList.find(i => i.Id === parseInt(d.ItemId));
        const returnedQty = parseFloat(d.ReturnStockQuantity) || 0;
        const soldQty = parseFloat(d.Quantity) || 0;
        const balanceStock = soldQty - returnedQty;
        return {
          id: Date.now() + Math.random(),
          itemId: d.ItemId,
          item: d.ItemName || d.itemName || d.Name || d.name || itemFromList?.Name || '',
          hsnCode: d.HSNCode || itemFromList?.HSNCode || '',
          gstPercent: d.GSTRate || 0,
          purchasedQty: soldQty,
          returnedQty: returnedQty,
          balanceStock: balanceStock,
          rcvdQty: 0,
          rate: d.Rate || 0,
          discPercent: d.DiscountPercentage || 0,
          discAmt: d.DiscountAmount || 0,
          taxable: d.TaxableAmount || 0,
          cgst: d.CGSTAmount || 0,
          sgst: d.SGSTAmount || 0,
          igst: d.IGSTAmount || 0,
          total: d.TotalAmount || 0
        };
      });

      setItems(mapped);
      calculateSummary(mapped, additionalCharges);
    } catch (e) {
      console.error(e);
      setItems([]);
    }
  };

  const handleItemChange = (id, field, value) => {
    if (field === 'discPercent' && parseFloat(value) > 100) return;
    const updated = items.map(item => {
      if (item.id !== id) return item;
      const upd = { ...item, [field]: value };
      if (field === 'itemId' && value) {
        const sel = itemsList.find(i => i.Id === parseInt(value));
        if (sel) {
          upd.item = sel.Name || '';
          upd.hsnCode = sel.HSNCode || '';
          upd.gstPercent = sel.GSTRate || 0;
          upd.rate = sel.SaleRate || sel.Rate || sel.MRP || 0;
        }
      }
      if (field === 'rcvdQty') {
        const rq = parseInt(value) || 0;
        if (rq < 0) upd.rcvdQty = 0;
        const maxReturnQty = upd.balanceStock !== undefined ? upd.balanceStock : upd.purchasedQty;
        if (maxReturnQty > 0 && rq > maxReturnQty) upd.rcvdQty = maxReturnQty;
      }
      if (['rcvdQty', 'rate', 'discPercent', 'discAmt', 'gstPercent', 'itemId'].includes(field)) {
        const qty = Math.max(0, parseFloat(upd.rcvdQty) || 0);
        const rate = Math.max(0, parseFloat(upd.rate) || 0);
        const gstRate = Math.max(0, parseFloat(upd.gstPercent) || 0);
        const useIGST = isIGSTInvoice();
        const base = isIncludingTax() ? (qty * rate) / (1 + gstRate / 100) : qty * rate;
        let discAmt, discPct;
        if (field === 'discAmt') {
          discAmt = Math.min(Math.max(0, parseFloat(value) || 0), base);
          discPct = base > 0 ? (discAmt / base) * 100 : 0;
          upd.discAmt = Math.round(discAmt * 100) / 100;
          upd.discPercent = Math.round(discPct * 100) / 100;
        } else if (field === 'discPercent') {
          discPct = Math.min(Math.max(0, parseFloat(value) || 0), 100);
          discAmt = (base * discPct) / 100;
          upd.discPercent = discPct;
          upd.discAmt = Math.round(discAmt * 100) / 100;
        } else {
          discPct = Math.min(Math.max(0, parseFloat(upd.discPercent) || 0), 100);
          discAmt = (base * discPct) / 100;
          upd.discAmt = Math.round(discAmt * 100) / 100;
        }
        const taxable = base - discAmt;
        const gstAmt = (taxable * gstRate) / 100;
        upd.taxable = Math.round(taxable * 100) / 100;
        upd.cgst = useIGST ? 0 : Math.round((gstAmt / 2) * 100) / 100;
        upd.sgst = useIGST ? 0 : Math.round((gstAmt / 2) * 100) / 100;
        upd.igst = useIGST ? Math.round(gstAmt * 100) / 100 : 0;
        upd.total = Math.round((taxable + gstAmt) * 100) / 100;
      }
      return upd;
    });
    setItems(updated);
    calculateSummary(updated, additionalCharges);
  };

  const handleAddItem = () => {
    if (items.length > 0) {
      const last = items[items.length - 1];
      if (!last.itemId) { setValidationError('Please select an item in the current row first.'); return; }
      if (!last.rcvdQty || parseFloat(last.rcvdQty) <= 0) { setValidationError('Please enter Return Qty first.'); return; }
    }
    setValidationError('');
    setItems(prev => [...prev, { id: Date.now(), itemId: '', item: '', hsnCode: '', purchasedQty: 0, rcvdQty: 0, rate: 0, discPercent: 0, discAmt: 0, gstPercent: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 }]);
  };

  const handleDeleteItem = (id) => {
    const updated = items.filter(i => i.id !== id);
    setItems(updated);
    calculateSummary(updated, additionalCharges);
  };

  const handleAdditionalChargeChange = (field, value) => {
    const numVal = parseFloat(value) || 0;
    setAdditionalCharges(prev => {
      const updated = { ...prev, [field]: numVal };
      calculateSummary(items, updated);
      return updated;
    });
  };

  const calculateSummary = (itemsList, charges) => {
    const useIGST = isIGSTInvoice();
    const maxGST = itemsList.reduce((m, i) => Math.max(m, parseFloat(i.gstPercent) || 0), 0);
    const effectiveGSTRate = Math.max(18, maxGST);
    const totals = itemsList.reduce((acc, item) => {
      const qty = parseFloat(item.rcvdQty) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discPct = parseFloat(item.discPercent) || 0;
      const gstRate = parseFloat(item.gstPercent) || 0;
      const base = isIncludingTax() ? (qty * rate) / (1 + gstRate / 100) : qty * rate;
      const discAmt = (base * discPct) / 100;
      const taxable = base - discAmt;
      const gstAmt = (taxable * gstRate) / 100;
      return {
        subtotal: acc.subtotal + qty * rate,
        discount: acc.discount + discAmt,
        taxable: acc.taxable + taxable,
        cgst: acc.cgst + (useIGST ? 0 : gstAmt / 2),
        sgst: acc.sgst + (useIGST ? 0 : gstAmt / 2),
        igst: acc.igst + (useIGST ? gstAmt : 0)
      };
    }, { subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0 });
    const freight = parseFloat(charges.freight) || 0;
    const packing = parseFloat(charges.packing) || 0;
    const tcs = parseFloat(charges.tcsAmount) || 0;
    const freightGST = (freight * effectiveGSTRate) / 100;
    const packingGST = (packing * effectiveGSTRate) / 100;
    const totalFP = freightGST + packingGST;
    const grand = totals.taxable + totals.cgst + totals.sgst + totals.igst + freight + packing + totalFP + tcs;
    const roundOff = Math.round(grand) - grand;
    setSummary({
      subtotal: Math.round(totals.subtotal * 100) / 100,
      discount: Math.round(totals.discount * 100) / 100,
      taxable: Math.round(totals.taxable * 100) / 100,
      cgst: Math.round((totals.cgst + (useIGST ? 0 : totalFP / 2)) * 100) / 100,
      sgst: Math.round((totals.sgst + (useIGST ? 0 : totalFP / 2)) * 100) / 100,
      igst: Math.round((totals.igst + (useIGST ? totalFP : 0)) * 100) / 100,
      freightGST: Math.round(freightGST * 100) / 100,
      packingGST: Math.round(packingGST * 100) / 100,
      effectiveGSTRate, tcs,
      roundOff: Math.round(roundOff * 100) / 100,
      total: Math.round(grand)
    });
  };

  const handleSave = async () => {
    setValidationError('');
    if (!formData.customerName) { setValidationError('Please select a customer'); return; }
    if (!formData.billNo.trim()) { setValidationError('Return bill number is required'); return; }
    if (!formData.billDate) { setValidationError('Return date is required'); return; }
    const returnItems = items.filter(i => (parseFloat(i.rcvdQty) || 0) > 0 && i.itemId && (i.balanceStock === undefined || i.balanceStock > 0));
    if (returnItems.length === 0) { setValidationError('No items available for return (all items have been fully returned)'); return; }
    setLoading(true);
    setLoadingMessage(mode === 'edit' ? 'Updating sale return...' : 'Creating sale return...');
    try {
      const useIGST = isIGSTInvoice();
      const srId = mode === 'edit' ? (editData?.SaleReturn?.Id || editData?.Id || 0) : 0;
      const payload = {
        SaleReturn: {
          Id: srId, CompanyId: companyId, BranchId: branchId,
          MasterId: parseInt(formData.customerId) || 0,
          CustomerName: formData.customerName, GSTNumber: formData.gstNo,
          SaleId: parseInt(formData.saleId) || 0,
          SaleNo: formData.saleNo || '',
          BillNumber: formData.billNo,
          BillPrefix: formData.billPrefix || '',
          FinYear: formData.finYear || '',
          BillDate: formData.billDate,
          InvoiceTypeName: formData.invType,
          InvoiceTypeId: formData.invoiceTypeId || null,
          GstCalculationType: formData.gstType === 'Including Tax' ? 2 : 1,
          GstCalculationTypeName: formData.gstType,
          Amount: summary.subtotal, DiscountAmount: summary.discount,
          TaxableAmount: summary.taxable,
          CGSTAmount: useIGST ? 0 : summary.cgst,
          SGSTAmount: useIGST ? 0 : summary.sgst,
          IGSTAmount: useIGST ? summary.igst : 0,
          FreightAmount: parseFloat(additionalCharges.freight) || 0,
          FreightGSTRate: summary.effectiveGSTRate, FreightGSTAmount: summary.freightGST,
          PackingAmount: parseFloat(additionalCharges.packing) || 0,
          PackingGSTRate: summary.effectiveGSTRate, PackingGSTAmount: summary.packingGST,
          TCSAmount: parseFloat(additionalCharges.tcsAmount) || 0,
          RoundOff: summary.roundOff, TotalAmount: summary.total,
          GrandTotal: summary.total,
          PaidAmount: parseFloat(additionalCharges.paidAmount) || 0,
          BalanceAmount: summary.total - (parseFloat(additionalCharges.paidAmount) || 0),
          Remarks: formData.remarks || '', IsActive: true,
          ...(mode === 'edit' ? { ModifiedBy: userId } : { CreatedBy: userId })
        },
        SaleReturnDetails: returnItems.map(item => ({
          Id: item.detailId || 0, CompanyId: companyId, BranchId: branchId, SaleReturnId: srId,
          ItemId: parseInt(item.itemId), ItemName: item.item || '',
          SaleId: parseInt(formData.saleId) || 0,
          SaleQuantity: parseFloat(item.purchasedQty) || 0,
          Quantity: parseFloat(item.rcvdQty) || 0,
          Rate: parseFloat(item.rate) || 0,
          Amount: (parseFloat(item.rcvdQty) || 0) * (parseFloat(item.rate) || 0),
          DiscountPercentage: parseFloat(item.discPercent) || 0,
          DiscountAmount: parseFloat(item.discAmt) || 0,
          HSNCode: item.hsnCode || '', GSTRate: parseFloat(item.gstPercent) || 0,
          TaxableAmount: parseFloat(item.taxable) || 0,
          CGSTAmount: parseFloat(item.cgst) || 0,
          SGSTAmount: parseFloat(item.sgst) || 0,
          IGSTAmount: parseFloat(item.igst) || 0,
          TotalAmount: parseFloat(item.total) || 0,
          IsActive: true,
          ...(mode === 'edit' ? { ModifiedBy: userId } : { CreatedBy: userId })
        }))
      };
      if (mode === 'edit') {
        await updateData('SaleReturnApi', srId, payload, token);
        setSuccess('Sale return updated successfully!');
      logAudit(AUDIT_ACTION.UPDATE, 'SaleReturnApi', srId, { ...editData }, { ...formData });
      } else {
        await postData('SaleReturnApi', '', payload, token);
        setSuccess('Sale return created successfully!');
      logAudit(AUDIT_ACTION.CREATE, 'SaleReturnApi', 0, null, { BillNumber: formData.BillNumber || '' });
      }
      fetchSaleReturns();
      handleClose();
    } catch (e) {
      console.error(e);
      setError(mode === 'edit' ? 'Failed to update sale return.' : 'Failed to create sale return.');
    }
    setLoading(false);
  };

  const handleClose = () => {
    setFormData({ billNo: '', billPrefix: '', finYear: '', billDate: new Date().toISOString().split('T')[0], saleId: '', saleNo: '', customerId: '', customerName: '', gstNo: '', invType: '', invoiceTypeId: null, gstType: 'Excluding Tax', remarks: '' });
    setItems([]); setSales([]);
    setAdditionalCharges({ freight: 0, packing: 0, tcsAmount: 0, paidAmount: 0 });
    setSummary({ subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, freightGST: 0, packingGST: 0, effectiveGSTRate: 18, tcs: 0, roundOff: 0, total: 0 });
    setValidationError('');
    onClose();
  };

  const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const ci = { '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } };
  const disabledStyle = { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: '10px', m: 2, maxWidth: '1400px' } }}>

      {/* Header - Sale green style */}
      <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #d1fae5', bgcolor: '#f0fdf4' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart sx={{ color: '#059669', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#064e3b', fontSize: '1rem' }}>{mode === 'edit' ? 'Edit Sale Return' : 'Create Sale Return'}</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}><Close fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2, bgcolor: '#fefffe' }}>
        {validationError && (
          <Box sx={{ mb: 1, p: 1, bgcolor: '#fee2e2', borderRadius: 1 }}>
            <Typography sx={{ color: '#dc2626', fontSize: '0.813rem' }}>{validationError}</Typography>
          </Box>
        )}

        {/* Header Form - Sale green style */}
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#059669' }}>Return Information</Typography>
        <Grid container spacing={1} sx={{ mb: 1.5, mt: 0.5 }}>
          <Grid item xs={6} sm={1.5}>
            <TextField fullWidth size="small" label="Return No *"
              value={formData.billPrefix && formData.finYear
                ? `${formData.billPrefix}/${formData.billNo}/${formData.finYear}`
                : formData.billNo || ''}
              InputProps={{ readOnly: true }}
              sx={{ ...ci, '& .MuiInputBase-input': { color: '#059669', fontWeight: 700, fontSize: '0.8rem', py: 0.75 } }} />
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField fullWidth size="small" type="date" label="Return Date *"
              value={formData.billDate}
              onChange={e => setFormData(p => ({ ...p, billDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={ci} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              size="small"
              options={customers}
              getOptionLabel={option => option.Name || ''}
              value={customers.find(c => (c.MasterId || c.Id) === formData.customerId) || null}
              onChange={(e, val) => {
                if (val) {
                  const custId = val.MasterId || val.Id;
                  setFormData(prev => ({
                    ...prev,
                    customerId: custId,
                    customerName: val.Name || '',
                    gstNo: val.GSTNumber || val.GSTNo || '',
                    invType: val.InvoiceTypeName || val.InvoiceType || '',
                    invoiceTypeId: val.InvoiceTypeId || val.invoiceTypeId || null,
                    saleId: '', saleNo: ''
                  }));
                  setItems([]);
                  calculateSummary([], additionalCharges);
                  fetchCustomerSales(custId);
                } else {
                  setFormData(prev => ({ ...prev, customerId: '', customerName: '', gstNo: '', invType: '', invoiceTypeId: null, saleId: '', saleNo: '' }));
                  setSales([]);
                  setItems([]);
                  calculateSummary([], additionalCharges);
                }
              }}
              renderInput={params => (
                <TextField {...params} size="small" label="Customer *"
                  sx={{ ...ci, '& .MuiInputBase-root': { height: '30px', minHeight: '30px', padding: '0 9px !important' }, '& .MuiInputBase-input': { padding: '0 !important', fontSize: '0.8rem', height: '30px' }, '& .MuiAutocomplete-endAdornment': { top: 'calc(50% - 12px)' } }} />
              )}
              isOptionEqualToValue={(option, value) => (option.MasterId || option.Id) === (value.MasterId || value.Id)}
              ListboxProps={{ style: { maxHeight: 200, fontSize: '0.85rem' } }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" select label="Against Sale"
              value={formData.saleId}
              onChange={e => handleSaleChange(e.target.value)}
              disabled={!formData.customerId || salesLoading}
              InputProps={salesLoading ? { endAdornment: <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment> } : {}}
              sx={ci}>
              <MenuItem value=""><em>Select Sale (optional)</em></MenuItem>
              {formData.saleId && !sales.some(s => (s.Id || s.SaleId) === formData.saleId) && (
                <MenuItem value={formData.saleId} sx={{ fontSize: '0.85rem' }}>{formData.saleNo || formData.saleId}</MenuItem>
              )}
              {sales.map((s, idx) => (
                <MenuItem key={s.Id || s.SaleId || idx} value={s.Id || s.SaleId} sx={{ fontSize: '0.85rem' }}>
                  {s.BillPrefix ? s.BillPrefix + '/' : ''}{s.BillNumber || s.BillNo || s.Id}
                  {s.BillDate ? ` — ${s.BillDate.split('T')[0]}` : ''}
                  {s.TotalAmount ? ` — ₹${parseFloat(s.TotalAmount).toFixed(2)}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField fullWidth size="small" label="GST No"
              value={formData.gstNo}
              disabled={!!formData.customerId}
              onChange={e => setFormData(p => ({ ...p, gstNo: e.target.value }))}
              sx={{ ...ci, ...disabledStyle, '& .MuiInputBase-root': { bgcolor: formData.gstNo ? '#dcfce7' : '#f9fafb' } }} />
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField fullWidth size="small" label="Inv Type"
              value={formData.invType} disabled
              sx={{ ...ci, ...disabledStyle, '& .MuiInputBase-root': { bgcolor: formData.invType ? '#d1fae5' : '#f9fafb' } }} />
          </Grid>

        </Grid>

        {/* Items Table */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#059669' }}>Return Items</Typography>
        </Box>

        <Box sx={{ bgcolor: 'white', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #d1fae5', mb: 2 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f0fdf4', borderBottom: '2px solid #bbf7d0' }}>
                  {['#', 'ITEM', 'HSN', 'SOLD QTY', 'PREV RET', 'BAL QTY', 'RETURN QTY *', 'RATE', 'AMOUNT', 'DISC %', 'DISC AMT', 'GST%', 'TAXABLE', 'GST AMT', 'TOTAL', ''].map((h, i) => (
                    <th key={i} style={{ padding: '6px 8px', textAlign: i >= 3 && i <= 14 ? 'right' : i === 15 ? 'center' : 'left', fontWeight: 700, color: '#065f46', fontSize: '0.65rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={16} style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '0.813rem' }}>
                    Select a customer and sale above to load return items.
                  </td></tr>
                ) : items.map((item, idx) => {
                  const dynamicBalance = (item.balanceStock !== undefined ? item.balanceStock : item.purchasedQty) - (parseFloat(item.rcvdQty) || 0);
                  const isZeroBalance = (item.balanceStock !== undefined && item.balanceStock <= 0);
                  const isFullyReturned = isZeroBalance || dynamicBalance <= 0;
                  return (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f0fdf4', background: isFullyReturned ? '#fef2f2' : 'transparent' }}>
                    <td style={{ padding: '4px 8px', fontSize: '0.8rem', fontWeight: 500 }}>{idx + 1}</td>
                    <td style={{ padding: '4px 8px', minWidth: 140 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: isFullyReturned ? '#dc2626' : '#064e3b' }}>{item.item || '-'}</Typography>
                    </td>
                    <td style={{ padding: '4px 8px', fontSize: '0.75rem', color: '#64748b' }}>{item.hsnCode || '-'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.8rem', fontWeight: 600, color: '#059669' }}>{item.purchasedQty || '-'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.8rem', fontWeight: 600, color: item.returnedQty > 0 ? '#dc2626' : '#64748b' }}>{item.returnedQty || 0}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.8rem', fontWeight: 700, color: dynamicBalance <= 0 ? '#dc2626' : '#059669' }}>{dynamicBalance}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {isFullyReturned ? (
                        <Typography sx={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 600 }}>Fully Returned</Typography>
                      ) : (
                      <TextField size="small" type="number" value={item.rcvdQty || ''}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          if (isNaN(val) || val < 0) return;
                          const maxQty = item.balanceStock !== undefined ? item.balanceStock : item.purchasedQty;
                          if (maxQty > 0 && val > maxQty) return;
                          handleItemChange(item.id, 'rcvdQty', val);
                        }}
                        inputProps={{ min: 0, max: item.balanceStock !== undefined ? item.balanceStock : item.purchasedQty, step: 1, style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 6px' } }}
                        sx={{ width: 75 }} />
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <TextField size="small" type="number" value={item.rate || ''}
                        onChange={e => handleItemChange(item.id, 'rate', e.target.value)}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 6px' } }}
                        sx={{ width: 85 }} />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.8rem', color: '#64748b' }}>{fmt((parseFloat(item.rcvdQty) || 0) * (parseFloat(item.rate) || 0))}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <TextField size="small" type="number" value={item.discPercent ?? 0}
                        onChange={e => handleItemChange(item.id, 'discPercent', e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 6px' } }}
                        sx={{ width: 65 }} />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <TextField size="small" type="number" value={item.discAmt ?? 0}
                        onChange={e => handleItemChange(item.id, 'discAmt', e.target.value)}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 6px' } }}
                        sx={{ width: 75 }} />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <TextField size="small" type="number" value={item.gstPercent || ''}
                        disabled
                        inputProps={{ style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 6px' } }}
                        sx={{ width: 60, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#374151', fontWeight: 600 } }} />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.8rem', color: '#64748b' }}>{fmt(item.taxable)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.8rem', color: '#dc2626', fontWeight: 600 }}>{fmt((item.cgst || 0) + (item.sgst || 0) + (item.igst || 0))}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.8rem', fontWeight: 700, color: '#064e3b' }}>{fmt(item.total)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      <IconButton size="small" onClick={() => handleDeleteItem(item.id)} sx={{ color: '#dc2626', p: 0.3 }}><Delete sx={{ fontSize: 15 }} /></IconButton>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Additional Charges + Summary + Remarks */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={5}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#059669', display: 'block', mb: 0.5 }}>Additional Charges</Typography>
            <Grid container spacing={1}>
              {[['Freight', 'freight'], ['Packing', 'packing'], ['TCS Amount', 'tcsAmount'], ['Paid Amount', 'paidAmount']].map(([label, field]) => (
                <Grid item xs={6} key={field}>
                  <TextField fullWidth size="small" label={label} type="number"
                    value={additionalCharges[field] || ''}
                    onChange={e => handleAdditionalChargeChange(field, e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }} sx={ci} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Remarks" multiline rows={2}
                  value={formData.remarks}
                  onChange={e => setFormData(p => ({ ...p, remarks: e.target.value }))} sx={ci} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#059669', display: 'block', mb: 0.5 }}>Summary</Typography>
            <Box sx={{ bgcolor: '#f0fdf4', borderRadius: 1.5, p: 1.5, border: '1px solid #bbf7d0' }}>
              <Grid container spacing={0.8}>
                {[
                  ['Subtotal', summary.subtotal, '#0f172a'],
                  ['Discount', summary.discount, '#dc2626'],
                  ['Taxable', summary.taxable, '#0f172a'],
                  [isIGSTInvoice() ? 'IGST' : 'CGST', isIGSTInvoice() ? summary.igst : summary.cgst, '#dc2626'],
                  ...(!isIGSTInvoice() ? [['SGST', summary.sgst, '#dc2626']] : []),
                  [`Freight GST`, summary.freightGST, '#dc2626'],
                  [`Packing GST`, summary.packingGST, '#dc2626'],
                  ['TCS', summary.tcs, '#0f172a'],
                  ['Round Off', summary.roundOff, '#0f172a'],
                ].map(([label, val, color]) => (
                  <Grid item xs={4} sm={2.4} key={label}>
                    <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: 'white', borderRadius: 1, border: '1px solid #d1fae5' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.6rem', fontWeight: 600, display: 'block' }}>{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color, fontSize: '0.75rem' }}>{fmt(val)}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#dcfce7', borderRadius: 1, border: '1.5px solid #86efac' }}>
                    <Typography variant="caption" sx={{ color: '#166534', fontSize: '0.6rem', fontWeight: 700 }}>RETURN TOTAL</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#15803d', fontSize: '1rem' }}>{fmt(summary.total)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#dbeafe', borderRadius: 1, border: '1.5px solid #93c5fd' }}>
                    <Typography variant="caption" sx={{ color: '#1e40af', fontSize: '0.6rem', fontWeight: 700 }}>BALANCE</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#2563eb', fontSize: '1rem' }}>{fmt(summary.total - (parseFloat(additionalCharges.paidAmount) || 0))}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Footer - Sale style */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #d1fae5', bgcolor: '#f0fdf4', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={handleClose}
          sx={{ textTransform: 'none', borderRadius: 1, px: 2.5, fontWeight: 600, fontSize: '0.875rem', borderColor: '#059669', color: '#059669', '&:hover': { bgcolor: '#f0fdf4' } }}>
          Cancel
        </Button>
        {!(items.length > 0 && items.every(i => i.balanceStock !== undefined && i.balanceStock <= 0)) && (
        <Button variant="contained" onClick={handleSave}
          sx={{ textTransform: 'none', borderRadius: 1, px: 2.5, fontWeight: 600, fontSize: '0.875rem', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
          {mode === 'edit' ? 'Update Return' : 'Save Return'}
        </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default SaleReturnModal;

